/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React, { useEffect, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../../../store/userSlice';
import { addLessonVersion } from '../../../../../app/actions/index';
// BASE components
import BASESvgIcon from '../../../../../@BASE/core/BASESvgIcon';
// forms
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import _ from '../../../../../@lodash';
// Material UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
// custom components
import FormSectionHeader from '../FormSectionHeader';

const schema = yup.object().shape({
  version: yup.string().required('You must select a version')
});

export default function VersionForm(props) {
  const { resource, instance } = useOutletContext();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [version, setVersion] = useState({ version: '', user: user._id });
  const [duplicateError, setDuplicateError] = useState('');
  // form
  const { control, watch, reset, handleSubmit, formState, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // on change set form data
  function onSubmit(data) {
    dispatch(
      addLessonVersion({
        lessonId: instance.id,
        data: {
          version: data.version,
          user: user._id
        }
      })
    );
  }
  // when the version changes, reset the form
  useEffect(() => {
    reset({ ...version });
  }, [version, reset]);

  return (
    <div className="relative flex flex-col flex-auto w-100">
      <FormSectionHeader
        title="Add Lesson Version"
        description="Select a lesson version below."
      />
      <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
        <div className="sm:col-span-4">
          <FormControl
            variant="outlined"
            fullWidth
            error={!!errors.version}
            required
          >
            <InputLabel id="project">Version</InputLabel>
            <Controller
              control={control}
              name="version"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <>
                  <Select
                    value={value}
                    onChange={(e) => {
                      const selectedVersion = e.target.value;
                      const savedVersions =
                        instance.archived_versions?.map((v) => v.version) || [];

                      if (savedVersions.includes(selectedVersion)) {
                        setDuplicateError('This version is already saved.');
                      } else {
                        setDuplicateError('');
                      }

                      onChange(e);
                    }}
                    label="Version"
                    placeholder="Version"
                    id="Version"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>
                      Select a version
                    </MenuItem>
                    <MenuItem value="dev_alpha" className="space-x-12">
                      Storyboard Alpha
                    </MenuItem>
                    <MenuItem value="dev_beta" className="space-x-12">
                      Storyboard Beta
                    </MenuItem>
                    <MenuItem value="dev_final" className="space-x-12">
                      Storyboard Final
                    </MenuItem>
                    <MenuItem value="prod_alpha" className="space-x-12">
                      Production Alpha
                    </MenuItem>
                    <MenuItem value="prod_beta" className="space-x-12">
                      Production Beta
                    </MenuItem>
                    <MenuItem value="prod_final" className="space-x-12">
                      Production Final
                    </MenuItem>
                    <MenuItem value="trial_indv" className="space-x-12">
                      Individual Trial
                    </MenuItem>
                    <MenuItem value="trial_grp" className="space-x-12">
                      Group Trial
                    </MenuItem>
                    <MenuItem value="final" className="space-x-12">
                      Final
                    </MenuItem>
                  </Select>
                  {duplicateError && (
                    <Typography variant="caption" color="error">
                      {duplicateError}
                    </Typography>
                  )}
                </>
              )}
            />
          </FormControl>
        </div>
      </div>
      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        <Button component={Link} className="ml-auto" to="../">
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid || !!duplicateError}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div>
  );
}
